import { Helmet } from 'react-helmet';
import type { VoidFunctionComponent } from 'react';

import { GLOBAL_LICENSE_CORRECTIONS, MANUAL_MALWARE, VULN_ADMIN } from '../../../shared/featureFlags/flagNames';
import { CacheKind } from '../../../shared/InternalTools/cache';
import { window } from '../../globals';
import { useCollapsibleSections } from '../../hooks/useCollapsibleSections';
import { HeaderWarning } from '../admin/HeaderWarning/HeaderWarning';
import CallToAction from '../CallToAction';
import Flex from '../generic/Flex';
import FeatureFlagRequired from '../generic/Gates/FeatureFlagRequired';
import PageHeadingBreadcrumbs from '../generic/PageHeadingBreadcrumbs';
import Text from '../generic/Text';

import BuildLocatorsComponent from './BuildLocatorsComponent';
import FaktoryInfo from './FaktoryInfo';
import InvalidateCacheComponent from './InvalidateCacheComponent';
import LicenseCorrectionCard from './LicenseCorrectionCard/LicenseCorrectionCard';
import { ManualJobsCard } from './ManualJobsCard';
import ManualMalwareComponent from './ManualMalware';
import OnPremOrgsComponent from './OnPremOrgsComponent';
import OrganizationCard from './OrganizationCard';
import EnrichMetadataComponent from './security/EnrichMetadataComponent';
import RecalculateVulnsComponent from './security/RecalculateVulnsComponent';
import UserCard from './UserCard';

/**
 * Container for the internal tools page.
 */
const InternalToolsPage: VoidFunctionComponent = () => {
  const { isSectionOpen, toggleSection } = useCollapsibleSections('internal-tools-sections');

  return (
    <>
      <CallToAction>
        <PageHeadingBreadcrumbs
          truncateAt={80}
          crumbs={[
            {
              to: '/internal-tools',
              text: 'Internal Tools',
            },
            {
              text: window.user?.organization?.title ?? 'Organization',
            },
          ]}
        />
      </CallToAction>
      <div className="container margin-top">
        <Helmet title="Internal Tools" />
        <HeaderWarning />
        <div className="internal-tools">
          <Flex direction="column" gap={12}>
            <OrganizationCard
              isOpen={isSectionOpen('organization')}
              onToggle={(isOpen) => toggleSection('organization', isOpen)}
            />
            <UserCard isOpen={isSectionOpen('user')} onToggle={(isOpen) => toggleSection('user', isOpen)} />
            <OnPremOrgsComponent
              isOpen={isSectionOpen('onprem')}
              onToggle={(isOpen) => toggleSection('onprem', isOpen)}
            />
            <InvalidateCacheComponent
              cacheKinds={Object.values(CacheKind)}
              isOpen={isSectionOpen('cache')}
              onToggle={(isOpen) => toggleSection('cache', isOpen)}
            />
            <BuildLocatorsComponent
              isOpen={isSectionOpen('buildLocators')}
              onToggle={(isOpen) => toggleSection('buildLocators', isOpen)}
            />
            <FeatureFlagRequired flagName={MANUAL_MALWARE}>
              <ManualMalwareComponent
                isOpen={isSectionOpen('malware')}
                onToggle={(isOpen) => toggleSection('malware', isOpen)}
              />
            </FeatureFlagRequired>
            <FeatureFlagRequired flagName={GLOBAL_LICENSE_CORRECTIONS}>
              <LicenseCorrectionCard
                isOpen={isSectionOpen('license')}
                onToggle={(isOpen) => toggleSection('license', isOpen)}
              />
            </FeatureFlagRequired>
            <FaktoryInfo isOpen={isSectionOpen('faktory')} onToggle={(isOpen) => toggleSection('faktory', isOpen)} />
            <ManualJobsCard
              isOpen={isSectionOpen('manualJobs')}
              onToggle={(isOpen) => toggleSection('manualJobs', isOpen)}
            />
            <FeatureFlagRequired flagName={VULN_ADMIN}>
              <Text.H1>Vulns Tools</Text.H1>
              <div className="internal-tools__vulns">
                <EnrichMetadataComponent type="revision" supportedFetchers={['npm', 'pip', 'mvn']} />
                <RecalculateVulnsComponent />
              </div>
            </FeatureFlagRequired>
          </Flex>
        </div>
      </div>
    </>
  );
};

export default InternalToolsPage;
